import type { NestedKeySort } from '../TreeSelect.types';
import { SelectItem } from '@/types/ui';
import { useOldTreeSelectOptions } from './useOldTreeSelectOptions';
import { useOldTreeSelectHandlers } from './useOldTreeSelectHandlers';
import { useOldTreeSelectRendering } from './useOldTreeSelectRendering';

type UseOldTreeSelectParams<T extends string> = {
  options: SelectItem<T>[];
  value: T[];
  onChange: (value: T[]) => void;
  collapseSingleChild?: boolean;
  selectionMode: 'standard' | 'indication';
  nestedKeySort: NestedKeySort;
  disabled?: boolean;
  treeListItemClassName?: string;
};

export function useOldTreeSelect<T extends string>({
  options,
  value,
  onChange,
  collapseSingleChild,
  selectionMode,
  nestedKeySort,
  disabled,
  treeListItemClassName,
}: UseOldTreeSelectParams<T>) {
  const { fixedOptions, descendantMap, nestedOptions, sortedNestedOptions, valueLabelList, searchOptionByPath } =
    useOldTreeSelectOptions<T>({ options, value, collapseSingleChild, selectionMode, nestedKeySort });

  const {
    searchInput,
    setSearchInput,
    searchResults,
    userOpenedBranches,
    setUserOpenedBranches,
    handleClickOption,
    handleToggleBranch,
  } = useOldTreeSelectHandlers<T>({
    value,
    onChange,
    nestedOptions,
    descendantMap,
    selectionMode,
    searchOptionByPath,
  });

  const { renderNestedOptions } = useOldTreeSelectRendering<T>({
    value,
    searchInput,
    searchResults,
    userOpenedBranches,
    selectionMode,
    disabled,
    treeListItemClassName,
    handleClickOption,
    handleToggleBranch,
  });

  return {
    fixedOptions,
    sortedNestedOptions,
    valueLabelList,
    searchInput,
    setSearchInput,
    searchResults,
    setUserOpenedBranches,
    renderNestedOptions,
  };
}
